<template>
  <v-dialog
    v-model="showDialog"
    max-width="450"
    content-class="card-chat-dialog"
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card class="card-chat-modal">
      <!-- Header -->
      <div class="chat-modal-header" :style="{ background: color || '#7C3AED' }">
        <div class="chat-header-info">
          <v-avatar size="40" class="mr-3">
            <img v-if="agentAvatar" :src="agentAvatar" :alt="agentName" />
            <span v-else class="white--text text-h6">{{ getInitials(agentName) }}</span>
          </v-avatar>
          <div>
            <h3 class="chat-agent-name">{{ agentName }}</h3>
            <span class="chat-status">Online</span>
          </div>
        </div>
        <v-btn icon small @click="closeDialog" class="close-btn">
          <v-icon class="material-icons-round">close</v-icon>
        </v-btn>
      </div>

      <!-- Auth Check View -->
      <div v-if="!isAuthenticated && !anonymousMode && step === 'auth'" class="auth-check-view">
        <div class="auth-icon-container">
          <v-icon size="60" color="#7C3AED" class="material-icons-round">chat</v-icon>
        </div>
        <h3 class="auth-title">Start a Conversation</h3>
        <p class="auth-subtitle">Are you a registered user?</p>
        
        <div class="auth-buttons">
          <v-btn block large rounded color="#7C3AED" dark class="mb-3" @click="goToLogin">
            Yes, Login
          </v-btn>
          <v-menu offset-y top :close-on-content-click="true">
            <template v-slot:activator="{ on, attrs }">
              <v-btn block large rounded outlined color="#7C3AED" class="mb-3" v-bind="attrs" v-on="on">
                No, Register
              </v-btn>
            </template>
            <v-list class="register-dropdown-list">
              <v-list-item @click="becomeAgent('referring')" class="register-list-item">
                <v-list-item-title>Become a Referring Agent</v-list-item-title>
              </v-list-item>
              <v-list-item @click="becomeAgent('sales')" class="register-list-item">
                <v-list-item-title>Become a Sales Agent</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn block large rounded text color="grey" @click="continueAsAnonymous">
            Continue as Guest
          </v-btn>
        </div>
      </div>

      <!-- Anonymous Name Input -->
      <div v-else-if="!isAuthenticated && anonymousMode && step === 'name'" class="anonymous-name-view">
        <h3 class="auth-title">What's your name?</h3>
        <p class="auth-subtitle">Enter your name to start chatting</p>
        
        <v-form ref="nameForm" v-model="nameFormValid">
          <v-text-field
            v-model="anonymousName"
            outlined
            dense
            placeholder="Your full name"
            :rules="[v => !!v || 'Name is required']"
            class="name-input"
          ></v-text-field>
          <v-text-field
            v-model="anonymousEmail"
            outlined
            dense
            placeholder="Email (optional)"
            :rules="[v => !v || /.+@.+\..+/.test(v) || 'Valid email required']"
            class="name-input"
          ></v-text-field>
        </v-form>
        
        <v-btn block large rounded color="#7C3AED" dark :disabled="!nameFormValid" @click="startChat">
          Start Chat
        </v-btn>
      </div>

      <!-- Chat View -->
      <div v-else class="chat-view">
        <!-- Messages Container -->
        <div class="messages-container" ref="messagesContainer">
          <div v-if="loading" class="text-center py-4">
            <v-progress-circular indeterminate color="#7C3AED" size="30"></v-progress-circular>
          </div>
          <div v-else-if="messages.length === 0" class="no-messages">
            <v-icon size="40" color="grey lighten-1" class="material-icons-round">chat_bubble_outline</v-icon>
            <p>Start a conversation with {{ agentName }}</p>
          </div>
          <template v-else>
            <div
              v-for="(msg, index) in messages"
              :key="index"
              :class="['message-bubble', isSentByMe(msg) ? 'sent' : 'received']"
            >
              <p class="message-text">{{ msg.message }}</p>
              <span class="message-time">{{ formatTime(msg.created_at) }}</span>
            </div>
          </template>
        </div>

        <!-- Message Input -->
        <div class="message-input-container">
          <v-text-field
            v-model="newMessage"
            outlined
            dense
            placeholder="Type a message..."
            hide-details
            class="message-input"
            @keyup.enter="sendMessage"
          >
            <template v-slot:append>
              <v-btn icon small color="blue" dark :loading="sending" @click="sendMessage" :disabled="!newMessage.trim()">
                <v-icon class="material-icons-round white--text">send</v-icon>
              </v-btn>
            </template>
          </v-text-field>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>

export default {
  name: "CardChatPopup",
  props: {
    value: { type: Boolean, default: false },
    agentId: { type: String, required: true },
    agentName: { type: String, default: "Agent" },
    agentAvatar: { type: String, default: "" },
    color: { type: String, default: "#7C3AED" }
  },
  data() {
    return {
      step: "auth",
      anonymousMode: false,
      anonymousName: "",
      anonymousEmail: "",
      nameFormValid: false,
      messages: [],
      newMessage: "",
      loading: false,
      sending: false,
      currentUserId: null,
      tempUserId: null,
      chatId: null,
      pollInterval: null
    };
  },
  computed: {
    showDialog: {
      get() { return this.value; },
      set(val) { this.$emit("input", val); }
    },
    isAuthenticated() {
      return !!localStorage.getItem("token");
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initChat();
      } else {
        this.stopPolling();
      }
    }
  },
  methods: {
    initChat() {
      if (this.isAuthenticated) {
        this.currentUserId = localStorage.getItem("user_id");
        this.step = "chat";
        this.fetchMessages();
        this.startPolling();
      } else {
        const storedTempId = localStorage.getItem("temp_user_id_" + this.agentId);
        if (storedTempId) {
          this.tempUserId = storedTempId;
          this.anonymousMode = true;
          this.step = "chat";
          this.fetchMessages();
          this.startPolling();
        } else {
          this.step = "auth";
          this.anonymousMode = false;
        }
      }
    },
    closeDialog() {
      this.showDialog = false;
      this.stopPolling();
    },
    goToLogin() {
      this.$router.push("/login");
      this.closeDialog();
    },
    becomeAgent(agentType) {
      this.$router.push({
        path: '/form-ref/' + this.agentId,
        query: { agent_type: agentType }
      });
      this.closeDialog();
    },
    continueAsAnonymous() {
      this.anonymousMode = true;
      this.step = "name";
    },
    startChat() {
      if (this.$refs.nameForm && !this.$refs.nameForm.validate()) return;
      this.step = "chat";
      this.fetchMessages();
      this.startPolling();
    },
    async fetchMessages(showLoading = true) {
      // Only show loading on initial fetch, not during polling
      if (showLoading && this.messages.length === 0) {
        this.loading = true;
      }
      try {
        const headers = this.isAuthenticated
          ? { Authorization: "Bearer " + localStorage.getItem("token") }
          : {};
        const response = await axios.post("/api/card-chat/get-messages", {
          agent_id: this.agentId,
          temp_user_id: this.tempUserId
        }, { headers });
        if (response.data.flag === 1) {
          const newMessages = response.data.data || [];
          // Only scroll if new messages arrived
          const shouldScroll = newMessages.length > this.messages.length;
          this.messages = newMessages;
          this.chatId = response.data.chat_id;
          if (shouldScroll) {
            this.$nextTick(() => this.scrollToBottom());
          }
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        this.loading = false;
      }
    },
    async sendMessage() {
      if (!this.newMessage.trim()) return;
      this.sending = true;
      try {
        const headers = this.isAuthenticated
          ? { Authorization: "Bearer " + localStorage.getItem("token") }
          : {};
        const payload = {
          agent_id: this.agentId,
          message: this.newMessage.trim()
        };
        if (!this.isAuthenticated) {
          payload.sender_name = this.anonymousName;
          payload.sender_email = this.anonymousEmail || null;
          payload.temp_user_id = this.tempUserId;
        }
        const response = await axios.post("/api/card-chat/send-message", payload, { headers });
        if (response.data.flag === 1) {
          if (response.data.data.is_anonymous && response.data.data.temp_user_id) {
            this.tempUserId = response.data.data.temp_user_id;
            localStorage.setItem("temp_user_id_" + this.agentId, this.tempUserId);
          }
          this.newMessage = "";
          this.fetchMessages();
        }
      } catch (error) {
        console.error("Error sending message:", error);
        this.$toast?.error("Failed to send message");
      } finally {
        this.sending = false;
      }
    },
    startPolling() {
      this.pollInterval = setInterval(() => {
        if (this.showDialog) this.fetchMessages(false);
      }, 5000);
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      if (container) container.scrollTop = container.scrollHeight;
    },
    getInitials(name) {
      return name.split(" ").map(n => n.charAt(0).toUpperCase()).join("").substring(0, 2);
    },
    formatTime(timestamp) {
      if (!timestamp) return "";
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },
    isSentByMe(msg) {
      // For authenticated users, check user_id
      if (this.isAuthenticated && msg.user_id === this.currentUserId) {
        return true;
      }
      // For anonymous users, check temp_user_id
      if (!this.isAuthenticated && msg.temp_user_id && msg.temp_user_id === this.tempUserId) {
        return true;
      }
      return false;
    }
  },
  beforeDestroy() {
    this.stopPolling();
  }
};
</script>

<style>
.card-chat-dialog {
  max-height: 80vh !important;
  height: 80vh !important;
  margin: auto !important;
}
</style>
<style scoped>
.card-chat-modal {
  border-radius: 16px !important;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
}
.chat-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  color: white;
  flex-shrink: 0;
}
.chat-header-info {
  display: flex;
  align-items: center;
}
.chat-agent-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  color: white;
}
.chat-status {
  font-size: 12px;
  opacity: 0.8;
}
.auth-check-view, .anonymous-name-view {
  padding: 32px 24px;
  text-align: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.auth-icon-container {
  margin-bottom: 16px;
}
.auth-title {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 8px;
  color: #333;
}
.auth-subtitle {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}
.auth-buttons {
  margin-top: 24px;
}
.register-dropdown-list {
  border-radius: 12px !important;
  padding: 8px 0;
}
.register-list-item {
  padding: 12px 20px;
}
.register-list-item:hover {
  background: #f5f5f5;
}
.chat-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow: hidden;
}
.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  background: #f5f5f5;
}
.no-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #999;
}
.no-messages p {
  margin-top: 8px;
  font-size: 14px;
}
.message-bubble {
  max-width: 80%;
  padding: 10px 14px;
  border-radius: 16px;
  margin-bottom: 8px;
  word-wrap: break-word;
}
.message-bubble.sent {
  background: #7C3AED;
  color: white;
  margin-left: auto;
  border-bottom-right-radius: 4px;
}
.message-bubble.received {
  background: white;
  color: #333;
  margin-right: auto;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}
.message-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.4;
}
.message-time {
  font-size: 10px;
  opacity: 0.7;
  display: block;
  margin-top: 4px;
  text-align: right;
}
.message-input-container {
  padding: 12px 16px;
  background: white;
  border-top: 1px solid #eee;
  flex-shrink: 0;
}
.message-input {
  border-radius: 24px;
}
.name-input {
  margin-bottom: 12px;
}
</style>

